// News stay plain text in the API. Only web URLs and [label](URL) are rendered.
export function newsLinkHref(value) {
  const raw = String(value || "").trim();
  if (!raw || /[\s\\<>]/.test(raw) || [...raw].some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) return null;
  const href = /^www\./i.test(raw) ? `https://${raw}` : raw;
  if (!/^https?:\/\//i.test(href)) return null;
  try {
    const url = new URL(href);
    return url.hostname ? href : null;
  } catch {
    return null;
  }
}

function trimUrlPunctuation(value) {
  const pairs = { ")": "(", "]": "[", "}": "{" };
  const balance = { "(": 0, "[": 0, "{": 0 };
  for (const character of value) {
    if (Object.hasOwn(balance, character)) balance[character] += 1;
    else if (Object.hasOwn(pairs, character)) balance[pairs[character]] -= 1;
  }
  let end = value.length;
  while (end > 0) {
    const character = value[end - 1];
    if (/[.,!?;:]/.test(character)) end -= 1;
    else if (pairs[character] && balance[pairs[character]] < 0) {
      balance[pairs[character]] += 1;
      end -= 1;
    } else break;
  }
  return value.slice(0, end);
}

export function parseNewsLinks(value) {
  const text = String(value || "");
  const tokens = [];
  const starts = /\[((?:\\.|[^[\]\\\n]){1,1000})\]\(|https?:\/\/|www\./gi;
  let cursor = 0;
  let match;
  while ((match = starts.exec(text))) {
    const start = match.index;
    let end;
    let label;
    let href;
    if (match[1] !== undefined) {
      let depth = 1;
      let index = starts.lastIndex;
      // Bound incomplete link scanning in long drafts.
      const limit = Math.min(text.length, index + 4096);
      for (; index < limit && depth; index += 1) {
        const character = text[index];
        if (/\s/.test(character)) break;
        if (character === "(") depth += 1;
        if (character === ")") depth -= 1;
      }
      if (depth) continue;
      end = index;
      label = match[1].replace(/\\([\\[\]])/g, "$1");
      href = newsLinkHref(text.slice(starts.lastIndex, end - 1));
    } else {
      const candidate = text.slice(start).match(/^[^\s<>"'«»]+/)[0];
      const url = trimUrlPunctuation(candidate);
      // Avoid treating a scheme in the middle of a word as a pasted URL.
      if (start > 0 && /[\p{L}\p{N}_@]/u.test(text[start - 1])) continue;
      end = start + url.length;
      label = url;
      href = newsLinkHref(url);
    }
    if (start > cursor) tokens.push({ text: text.slice(cursor, start) });
    tokens.push(href ? { text: label, href } : { text: text.slice(start, end) });
    cursor = end;
    starts.lastIndex = end;
  }
  if (cursor < text.length) tokens.push({ text: text.slice(cursor) });
  return tokens;
}

export function plainNewsText(value) {
  return parseNewsLinks(value).map((token) => token.text).join("");
}

export function createNewsLink(label, href) {
  const text = (label.trim() || href).replace(/[\\[\]]/g, "\\$&");
  // Encoded parentheses keep the stored syntax unambiguous for any URL.
  const url = href.replace(/\(/g, "%28").replace(/\)/g, "%29");
  return `[${text}](${url})`;
}
